// @flow
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { INK_TOOLING_MEDIA_URL } from './utils/constants';

const CardContainer = styled.div`
  background-color: ${({ theme, bgColor }) => bgColor || theme.colors.backgroundLight};
  border-radius: 8px;
  padding: 16px 24px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CardTopSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const CardIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textPrimary};
  flex-shrink: 0;
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.neutralBlack};
  flex-wrap: wrap;
  margin: 0;
`;

const CardActionButton = styled.button`
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.black};
  background: ${({ theme }) => theme.colors.white};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.black};
  font-weight: 700;
  cursor: pointer;
  display: flex;
  height: 32px;
  padding: 0 16px;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundHover};
    text-decoration: none;
    color: ${({ theme }) => theme.colors.deepIndigo};
    font-weight: 700;
  }
`;

const CardLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardLink = styled.button`
  color: ${({ theme }) => theme.colors.mainFontColor};
  text-decoration: none;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  background: ${({ theme }) => theme.colors.white};
  padding: 8px 16px;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px
  text-decoration: none;

  &:hover {
    color: #1E163B;
    text-decoration: none;
    font-weight: 700;
  }
`;

type ToolCardProps = {
  id: string;
  title: string;
  bgColor: string;
  icon: React.Node;
  actionButton?: string;
  links?: string[];
  hasLiveBadge?: boolean;
  route: string;
};

const ToolCard: React.FC<ToolCardProps> = ({
  id,
  title,
  bgColor,
  icon,
  actionButton,
  links,
  hasLiveBadge,
  route
}) => {
  return (
    <CardContainer bgColor={bgColor}>
      <CardHeader>
        <CardTopSection>
          <CardIcon>{icon}</CardIcon>
          <CardTitle>
            {title}
          </CardTitle>
        </CardTopSection>
        {actionButton && (
          <CardActionButton as={Link} to={route}>
            {actionButton}
          </CardActionButton>
        )}
      </CardHeader>
      {links && links.length > 0 && (
        <CardLinks>
          {links.map((link, index) => (
            <CardLink key={index} as={Link} to={link?.route} target={link?.target || '_self'}>
              {link?.text}
              <img width="12" src={`${INK_TOOLING_MEDIA_URL}/open-caret.svg`} alt="open" />
            </CardLink>
          ))}
        </CardLinks>
      )}
    </CardContainer>
  );
};

export default React.memo(ToolCard);
